import React from "react";
import { apiUrl } from "../config/apiBase";
import "../App.css";
import Navbar from "./Navbar_menu";
import { formatujPercentoBezZnamienkaText, formatujPercentoText } from "../utils/formatovanie";
import PrehladZliavPrirazokKarta from "./PrehladZliavPrirazokKarta";
import KampaneKarta from "./KampaneKarta";
import HintPopover from "./HintPopover";
import { ziskajLogoBanky, ziskajStylLogaBanky } from "../utils/bankoveLoga";

export default function UrokyDetailStranka() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const [prehled, setPrehled] = React.useState(null);
  const [prehledError, setPrehledError] = React.useState("");
  const [kampaneData, setKampaneData] = React.useState(null);
  const [kampaneError, setKampaneError] = React.useState("");
  const [rezimKampane, setRezimKampane] = React.useState("AKTUALNE");

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const controller = new AbortController();
        const t = setTimeout(() => controller.abort(), 8000);

        const res = await fetch(apiUrl("/api/uroky/detail"), { signal: controller.signal });
        clearTimeout(t);

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!mounted) return;

        setData(json);
        try {
          setPrehledError("");
          const res2 = await fetch(apiUrl("/api/uroky/prehled-zlavy-prirazky"));
          if (res2.ok) {
            const json2 = await res2.json();
            if (mounted) setPrehled(json2);
          } else {
            if (mounted) setPrehledError("Nepodarilo sa načítať prehľad zliav a prirážok.");
          }
        } catch (_e2) {
          if (mounted) setPrehledError("Nepodarilo sa načítať prehľad zliav a prirážok.");
        }
      } catch (e) {
        if (!mounted) return;

        if (e?.name === "AbortError") {
            setError("Načítanie trvá príliš dlho (timeout). Skontroluj pripojenie na databázu.");
        } else {
            setError("Nepodarilo sa načítať detail úrokov.");
        }
        } finally {
            if (!mounted) return;
            setLoading(false);
        }
       
    })();

    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setKampaneError("");

        const res = await fetch(apiUrl(`/api/uroky/kampane?rezim=${encodeURIComponent(rezimKampane)}`));
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();
        if (!mounted) return;

        setKampaneData(json);
      } catch (_e) {
        if (!mounted) return;
        setKampaneError("Nepodarilo sa načítať kampane.");
      }
    })();

    return () => {
      mounted = false;
    };
  }, [rezimKampane]);

  const fixacie = Array.isArray(data?.fixacie) ? data.fixacie : [];

  const banky = Array.isArray(data?.banky) ? data.banky : [];
  const prehladBanky = Array.isArray(prehled?.banky) ? prehled.banky : [];  
  const kampaneBanky = Array.isArray(kampaneData?.banky) ? kampaneData.banky : [];

  const kampaneKarty = kampaneBanky.flatMap((x) => {
    const bankaKod = x?.bankaKod ?? "";
    const vsetkyTypyHypoteky = Array.isArray(x?.vsetkyTypyHypoteky) ? x.vsetkyTypyHypoteky : [];
    const kampane = Array.isArray(x?.kampane) ? x.kampane : [];

    if (kampane.length === 0) {
      return [{
        bankaKod,
        kampan: null,
        vsetkyTypyHypoteky
      }];
    }

    return kampane.map((kampan, idx) => ({
      bankaKod,
      kampan,
      vsetkyTypyHypoteky,
      key: `${bankaKod}-${kampan?.nazov || "kampan"}-${idx}`
    }));
  });

  const prehladSliderRef = React.useRef(null);
  const prehladWrapperRef = React.useRef(null);
  const [zobrazPrehladSipky, setZobrazPrehladSipky] = React.useState(false);

  const skontrolujPrehladSipky = React.useCallback(() => {
    const el = prehladSliderRef.current;
    if (!el) return;
    setZobrazPrehladSipky(el.scrollWidth > el.clientWidth + 2);
  }, []);

  React.useEffect(() => {
    skontrolujPrehladSipky();

    const el = prehladSliderRef.current;
    if (!el) return;

    const onScroll = () => skontrolujPrehladSipky();
    el.addEventListener("scroll", onScroll, { passive: true });

    const ro = new ResizeObserver(() => skontrolujPrehladSipky());
    ro.observe(el);
    if (prehladWrapperRef.current) ro.observe(prehladWrapperRef.current);

    window.addEventListener("resize", skontrolujPrehladSipky);

    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
      window.removeEventListener("resize", skontrolujPrehladSipky);
    };
  }, [prehladBanky?.length, skontrolujPrehladSipky]);

  const prehladScrollLeft = () => {
    if (!prehladSliderRef.current) return;
    prehladSliderRef.current.scrollBy({ left: -320, behavior: "smooth" });
  };

  const prehladScrollRight = () => {
    if (!prehladSliderRef.current) return;
    prehladSliderRef.current.scrollBy({ left: 320, behavior: "smooth" });
  };

  const kampaneSliderRef = React.useRef(null);
  const kampaneWrapperRef = React.useRef(null);
  const [zobrazKampaneSipky, setZobrazKampaneSipky] = React.useState(false);

  const skontrolujKampaneSipky = React.useCallback(() => {
    const el = kampaneSliderRef.current;
    if (!el) return;
    setZobrazKampaneSipky(el.scrollWidth > el.clientWidth + 2);
  }, []);

  React.useEffect(() => {
    skontrolujKampaneSipky();

    const el = kampaneSliderRef.current;
    if (!el) return;

    const onScroll = () => skontrolujKampaneSipky();
    el.addEventListener("scroll", onScroll, { passive: true });

    const ro = new ResizeObserver(() => skontrolujKampaneSipky());
    ro.observe(el);
    if (kampaneWrapperRef.current) ro.observe(kampaneWrapperRef.current);

    window.addEventListener("resize", skontrolujKampaneSipky);

    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
      window.removeEventListener("resize", skontrolujKampaneSipky);
    };
  }, [kampaneBanky?.length, skontrolujKampaneSipky]);

  const kampaneScrollLeft = () => {
    if (!kampaneSliderRef.current) return;
    kampaneSliderRef.current.scrollBy({ left: -320, behavior: "smooth" });
  };

  const kampaneScrollRight = () => {
    if (!kampaneSliderRef.current) return;
    kampaneSliderRef.current.scrollBy({ left: 320, behavior: "smooth" });
  };

  const tooltipPreKampan = (cell) => {
    const kampane = Array.isArray(cell?.kampaneUplatneneNaSadzbu)
      ? cell.kampaneUplatneneNaSadzbu
      : [];

    const kampaneInfo = Array.isArray(cell?.kampaneDoHintu)
      ? cell.kampaneDoHintu
      : [];

    const zlavy = Array.isArray(cell?.zlavyCheckboxy) ? cell.zlavyCheckboxy : [];

    // Tooltip má byť pri kampani alebo keď existujú započítané zľavy
    if (!kampane.length && !kampaneInfo.length && zlavy.length === 0) return "";

    const riadky = [];

    if (kampane.length || kampaneInfo.length) {
      riadky.push("Kampaň:");

      kampane.forEach((k) => {
        const nazov =
          k?.nazov ? String(k.nazov) : "";

        const typ =
          k?.typ ? String(k.typ) : "";

        const hodnota = k?.hodnota;

        const hintText =
          k?.hintText
            ? String(k.hintText).trim()
            : "";

        if (
          typ === "PEVNA" &&
          Number.isFinite(hodnota)
        ) {
          riadky.push(
            `• ${nazov}: pevná sadzba ${formatujPercentoBezZnamienkaText(hodnota)}`
          );

          if (hintText) {
            riadky.push(`  ${hintText}`);
          }

          return;
        }

        if (
          typ === "ZLAVA" &&
          Number.isFinite(hodnota)
        ) {
          riadky.push(
            `• ${nazov}: zľava ${formatujPercentoText(hodnota)}`
          );

          if (hintText) {
            riadky.push(`  ${hintText}`);
          }

          return;
        }

        riadky.push(`• ${nazov}`.trim());

        if (hintText) {
          riadky.push(`  ${hintText}`);
        }
      });

      // INFO / poplatkové kampane – len do hintu
      kampaneInfo.forEach((k) => {
        const nazov = k?.nazov ? String(k.nazov) : "";
        const hintText = k?.hintText ? String(k.hintText).trim() : "";

        if (hintText) {
          riadky.push(`• ${nazov}: ${hintText}`);
          return;
        }

        // fallback (ak by niekto nemal hintText)
        riadky.push(`• ${nazov}`.trim());
      });
    }

    //const zlavy = Array.isArray(cell?.zlavyCheckboxy) ? cell.zlavyCheckboxy : [];
    if (zlavy.length) {
      riadky.push("");
      riadky.push("Započítané zľavy:");

      const ucetZlavy = [];
      let ucetPoznamka = null;
      const ostatneZlavy = [];

      zlavy.forEach((z) => {
        const nazov = z?.nazov ? String(z.nazov) : "";
        const hodnota = z?.hodnota;
        const poznamka = z?.poznamka ? String(z.poznamka) : "";

        const riadok = Number.isFinite(hodnota)
          ? `• ${nazov}: ${formatujPercentoText(hodnota)}`
          : `• ${nazov}`;

        // účet + príjem patria spolu
        if (nazov === "účet" || nazov === "príjem") {
          ucetZlavy.push(riadok);
          if (poznamka) ucetPoznamka = poznamka;
        } else {
          ostatneZlavy.push(riadok);
          if (poznamka) ostatneZlavy.push(`  ${poznamka}`);
        }
      });

      // Najprv účet + príjem
      ucetZlavy.forEach((r) => riadky.push(r));

      // Potom ich spoločná poznámka
      if (ucetPoznamka) {
        riadky.push(`  ${ucetPoznamka}`);
      }

      // A potom ostatné zľavy
      ostatneZlavy.forEach((r) => riadky.push(r));
          }

    return riadky.join("\n").trim();
  };

  if (loading) {
    return (
    <>
        <Navbar />
        <div className="App">
        <div className="board">
            <div className="col card--full">
            <div className="card-section">
                <h2>Detail úrokových sadzieb</h2>
                <div className="table-wrapper">
                <table className="uroky-table">
                    </table>
                </div>
            </div>
        </div>
    </div>
</div>
</>
);
  }

  if (error) {
    return (
      <>
        <Navbar />
        <div className="App">
          <div className="board">
            <div className="col card--full">
              <div className="card-section">
                <h2>Detail úrokových sadzieb</h2>
                <div className="warning-text">{error}</div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="App">
        <div className="board">
          <div className="col card--full">
            <div className="card-section">
              <h2>Detail úrokových sadzieb</h2>

              <div className="uroky-detail-table-wrap">
                <table className="uroky-detail-table">
                  <thead>
                    <tr>
                      <th className="uroky-detail-table__th uroky-detail-table__th--banka"></th>
                      {banky.map((b) => {
                        const bankaKod = b?.bankaKod ?? "";
                        const logoBanky = ziskajLogoBanky(bankaKod);
                        const stylLogaBanky = ziskajStylLogaBanky(logoBanky);
                        return (
                          <th key={`th-${bankaKod}`} className="uroky-detail-table__th">
                            <div className="uroky-detail-table__banka-head">
                              {logoBanky ? (
                                <span
                                  className={`banka-logo-obal banka-logo-obal--${logoBanky.variant || "wide"}`}
                                  style={stylLogaBanky}
                                >
                                  {logoBanky.srcDark ? (
                                    <>
                                      <img
                                        src={logoBanky.src}
                                        alt={`${bankaKod} logo`}
                                        className={`banka-logo banka-logo--light banka-logo--fit-${logoBanky.fit || "contain"}`}
                                      />
                                      <img
                                        src={logoBanky.srcDark}
                                        alt={`${bankaKod} logo`}
                                        className={`banka-logo banka-logo--dark banka-logo--fit-${logoBanky.fit || "contain"}`}
                                      />
                                    </>
                                  ) : (
                                    <img
                                      src={logoBanky.src}
                                      alt={`${bankaKod} logo`}
                                      className={`banka-logo banka-logo--fit-${logoBanky.fit || "contain"}`}
                                    />
                                  )}
                                </span>
                              ) : null}

                              {/* <span>{bankaKod}</span> */}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>

                  <tbody>
                    {fixacie.map((f) => {
                      const key = f?.key ?? String(f);
                      const label = f?.label ?? String(f);

                      return (
                        <tr key={key}>
                          {/* FIXÁCIA stĺpec */}
                          <td className="uroky-detail-table__td uroky-detail-table__td--banka">
                            <div style={{ fontWeight: 800 }}>{label}</div>
                          </td>

                          {banky.map((b) => {
                            const bankaKod = b?.bankaKod ?? "";
                            const sadzby = b?.sadzby || {};
                            const cell = sadzby[key] || null;

                            const urok = Number.isFinite(cell?.urokPoKampani)
                              ? cell.urokPoKampani
                              : (Number.isFinite(cell?.urokZaklad) ? cell.urokZaklad : null);

                            const jeKampan = cell?.upraveneKampaniou === true;
                            const maInfoKampan = Array.isArray(cell?.kampaneDoHintu) && cell.kampaneDoHintu.length > 0;
                            const maZlavyCheckboxy =
                              Array.isArray(cell?.zlavyCheckboxy) &&
                              cell.zlavyCheckboxy.length > 0;

                            const tipCell =
                              (jeKampan || maInfoKampan || maZlavyCheckboxy)
                                ? tooltipPreKampan(cell)
                                : "";

                            return (
                              <td
                                key={`${key}-${bankaKod}`}
                                className={[
                                  "uroky-detail-table__td",
                                  !Number.isFinite(urok)
                                    ? "uroky-detail-table__td--empty"
                                    : "",
                                  (jeKampan || maInfoKampan)
                                    ? "uroky-detail-table__td--kampan"
                                    : "",
                                  tipCell
                                    ? "uroky-detail-table__td--interactive"
                                    : ""
                                ].join(" ")}
                              >
                                {Number.isFinite(urok) ? (
                                  tipCell ? (
                                    <HintPopover
                                      obsah={tipCell}
                                      ariaLabel={`Zobraziť detail sadzby ${bankaKod} ${label}`}
                                      pozicia="bottom"
                                      wrapperClassName="uroky-detail-table__hint-wrap"
                                      triggerClassName="uroky-detail-table__hint-trigger"
                                    >
                                      {formatujPercentoBezZnamienkaText(urok)}
                                    </HintPopover>
                                  ) : (
                                    formatujPercentoBezZnamienkaText(urok)
                                  )
                                ) : (
                                  "—"
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

            </div>
          </div>

          <div className="col card--full">
            <div className="card-section">
              <h2>Prehľad zliav a prirážok</h2>

              {prehledError ? (
                <div className="warning-text">{prehledError}</div>
              ) : null}

              <div className="porovnavac-slider-wrapper" ref={prehladWrapperRef}>
                {zobrazPrehladSipky && (
                  <button
                    type="button"
                    className="slider-arrow left"
                    onClick={prehladScrollLeft}
                    aria-label="Doľava"
                  >
                    ‹
                  </button>
                )}

                <div className="porovnavac-slider" ref={prehladSliderRef}>
                  {prehladBanky.map((x) => {
                    const bankaKod = x?.bankaKod ?? "";

                    return (
                      <PrehladZliavPrirazokKarta
                        key={`prehled-karta-${bankaKod}`}
                        bankaKod={bankaKod}
                        prehlad={x}
                      />
                    );
                  })}
                </div>

                {zobrazPrehladSipky && (
                  <button
                    type="button"
                    className="slider-arrow right"
                    onClick={prehladScrollRight}
                    aria-label="Doprava"
                  >
                    ›
                  </button>
                )}
              </div>
              
            </div>

            <div className="col card--full">
            <div className="card-section">
              <div className="prepay-head">
                <h2>Kampane</h2>

                {/* prepínač režimu – BEZ selectu */}
                <div className="pp-stepper">
                  <button
                    type="button"
                    className={`pp-step ${rezimKampane === "VSETKY" ? "is-active" : ""}`}
                    onClick={() => setRezimKampane("VSETKY")}
                  >
                    Všetky
                  </button>

                  <button
                    type="button"
                    className={`pp-step ${rezimKampane === "AKTUALNE" ? "is-active" : ""}`}
                    onClick={() => setRezimKampane("AKTUALNE")}
                  >
                    Aktuálne
                  </button>
                </div>
              </div>

              {kampaneError ? (
                <div className="warning-text">{kampaneError}</div>
              ) : null}

              <div className="porovnavac-slider-wrapper" ref={kampaneWrapperRef}>
                {zobrazKampaneSipky && (
                  <button
                    type="button"
                    className="slider-arrow left"
                    onClick={kampaneScrollLeft}
                    aria-label="Doľava"
                  >
                    ‹
                  </button>
                )}

                <div className="porovnavac-slider" ref={kampaneSliderRef}>
                  {kampaneKarty.map((x, idx) => (
                    <KampaneKarta
                      key={x?.key || `kampane-karta-${x?.bankaKod || "banka"}-${idx}`}
                      bankaKod={x?.bankaKod ?? ""}
                      kampane={x?.kampan ? [x.kampan] : []}
                      vsetkyTypyHypoteky={Array.isArray(x?.vsetkyTypyHypoteky) ? x.vsetkyTypyHypoteky : []}
                    />
                  ))}
                </div>

                {zobrazKampaneSipky && (
                  <button
                    type="button"
                    className="slider-arrow right"
                    onClick={kampaneScrollRight}
                    aria-label="Doprava"
                  >
                    ›
                  </button>
                )}
              </div>
            </div>
          </div>

          </div>

        </div>
      </div>
    </>
  );
}